import apiClient from "../lib/api-client";
import type {
  CanonicalPaymentAttemptResponse,
  SaleTenderRequest,
} from "@medicorepay/shared";
import { productRevivalApi } from "./product-revival-client";

const MANUAL_EXTERNAL_PROVIDER = "MANUAL_EXTERNAL";

// ═══════════════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════════════

export interface RazorpayConfig {
  keyId: string;
  isConfigured: boolean;
}

export interface CreateOrderResponse {
  orderId: string;
  amount: number;
  currency: string;
  keyId: string;
  subscriptionPaymentId: string;
  prefill?: {
    name?: string;
    email?: string;
    contact?: string;
  };
  notes?: Record<string, string>;
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  type: string;
  description: string | null;
  monthlyPrice: number | null;
  yearlyPrice: number | null;
  features: string[];
  limits: {
    maxStores: number;
    maxUsers: number;
    storageGB: number;
  };
}

export interface VerifyPaymentRequest {
  razorpayOrderId: string;
  razorpayPaymentId: string;
  razorpaySignature: string;
}

export interface VerifyPaymentResponse {
  success: boolean;
  message: string;
}

export interface PaymentHistoryItem {
  id: string;
  type: "subscription" | "invoice";
  amount: number;
  currency: string;
  status: string;
  method?: string;
  reference?: string;
  paidAt?: string;
  createdAt: string;
  description?: string;
}

export interface PaymentHistoryResponse {
  items: PaymentHistoryItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface AuthoritativeCashChangeOptions {
  possible: boolean;
  notes: Array<{ denominationPaise: number; count: number }>;
  shortPaise: number;
  alternatives: string[];
}

// ═══════════════════════════════════════════════════════════════════════════════════
// Razorpay Checkout Types
// ═══════════════════════════════════════════════════════════════════════════════════

export interface RazorpayCheckoutOptions {
  key: string;
  amount: number;
  currency: string;
  name: string;
  description?: string;
  order_id: string;
  prefill?: {
    name?: string;
    email?: string;
    contact?: string;
  };
  notes?: Record<string, string>;
  theme?: {
    color?: string;
  };
  handler?: (response: RazorpayPaymentResponse) => void;
  modal?: {
    ondismiss?: () => void;
  };
  config?: {
    display: {
      blocks: Record<
        string,
        { name: string; instruments: Array<{ method: "upi" | "card" }> }
      >;
      sequence: string[];
      preferences: { show_default_blocks: boolean };
    };
  };
}

export interface RazorpayPaymentResponse {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}

declare global {
  interface Window {
    Razorpay: new (options: RazorpayCheckoutOptions) => {
      open: () => void;
      close: () => void;
    };
  }
}

// ═══════════════════════════════════════════════════════════════════════════════════
// Payment Service
// ═══════════════════════════════════════════════════════════════════════════════════

export const paymentService = {
  async getAuthoritativeCashChangeOptions(
    sessionId: string,
    changePaise: number,
  ): Promise<AuthoritativeCashChangeOptions> {
    const response = await apiClient.get(
      "/payment-operations/cash/change-options",
      { params: { sessionId, changePaise } },
    );
    return response.data;
  },

  // Configuration
  async getRazorpayConfig(): Promise<RazorpayConfig> {
    const response = await apiClient.get("/payments/config");
    return response.data;
  },

  // Subscription Payments
  async listSubscriptionPlans(): Promise<SubscriptionPlan[]> {
    const response = await apiClient.get("/payments/subscription/plans");
    return response.data;
  },

  async createSubscriptionOrder(
    targetPlanId: string,
    interval: "monthly" | "yearly",
  ): Promise<CreateOrderResponse> {
    const response = await apiClient.post(
      "/payments/subscription/create-order",
      {
        targetPlanId,
        interval,
      },
      { headers: { "Idempotency-Key": crypto.randomUUID() } },
    );
    return response.data;
  },

  async verifySubscriptionPayment(
    data: VerifyPaymentRequest,
  ): Promise<VerifyPaymentResponse> {
    const response = await apiClient.post(
      "/payments/subscription/verify",
      data,
      { headers: { "Idempotency-Key": crypto.randomUUID() } },
    );
    return response.data;
  },

  async getPaymentHistory(params?: {
    page?: number;
    limit?: number;
    status?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<PaymentHistoryResponse> {
    const response = await apiClient.get("/payments/subscription/history", {
      params,
    });
    return response.data;
  },

  // Razorpay Checkout Helper
  loadRazorpayScript(): Promise<boolean> {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }

      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  },

  openCheckout(options: RazorpayCheckoutOptions): void {
    const razorpay = new window.Razorpay(options);
    razorpay.open();
  },

  async collectCanonicalSaleTenders(input: {
    quoteId: string;
    billingSessionId: string;
    customerId?: string | null;
    tenders: SaleTenderRequest[];
    clientApplication: "web" | "desktop";
  }): Promise<SaleTenderRequest[]> {
    const completed: SaleTenderRequest[] = [];
    for (const [index, tender] of input.tenders.entries()) {
      if (!["UPI", "CARD", "BANK_TRANSFER", "CHEQUE"].includes(tender.method)) {
        completed.push(tender);
        continue;
      }
      const storageKey = [
        "medicorepay:payment",
        input.quoteId,
        index,
        tender.method,
        tender.amountPaise,
      ].join(":");
      const idempotencyKey =
        sessionStorage.getItem(storageKey) || crypto.randomUUID();
      sessionStorage.setItem(storageKey, idempotencyKey);
      const attempt = await productRevivalApi.createCanonicalPaymentAttempt(
        {
          quoteId: input.quoteId,
          billingSessionId: input.billingSessionId,
          ...(input.customerId ? { customerId: input.customerId } : {}),
          method: tender.method,
          amountPaise: tender.amountPaise,
          appliedPaise: tender.amountPaise,
          ...(tender.reference ? { reference: tender.reference } : {}),
          ...(["UPI", "CARD"].includes(tender.method)
            ? { provider: MANUAL_EXTERNAL_PROVIDER }
            : {}),
          metadata: { channel: input.clientApplication, tenderIndex: index },
        },
        { idempotencyKey, clientApplication: input.clientApplication },
      );
      if (tender.method === "BANK_TRANSFER" || tender.method === "CHEQUE") {
        if (attempt.status !== "PENDING" && attempt.status !== "CLEARED")
          throw new Error(
            `${tender.method} receipt cannot be used in ${attempt.status}`,
          );
      } else if (!this.isCapturedCanonicalAttempt(attempt)) {
        throw new Error(
          `${tender.method} manual payment could not be confirmed (${attempt.status})`,
        );
      }
      sessionStorage.removeItem(storageKey);
      completed.push({ ...tender, paymentAttemptId: attempt.id });
    }
    return completed;
  },

  isCapturedCanonicalAttempt(
    attempt: CanonicalPaymentAttemptResponse,
  ): boolean {
    if (attempt.status === "EXCESS_COLLECTION")
      throw new Error(
        "This provider payment arrived after another tender completed the sale; resolve the excess collection before retrying.",
      );
    if (
      [
        "FAILED",
        "TIMED_OUT",
        "VOIDED",
        "REVERSED",
        "REFUNDED",
        "CHARGEBACK",
      ].includes(attempt.status)
    )
      throw new Error(`Payment attempt ended in ${attempt.status}`);
    return attempt.status === "SUCCESS" || attempt.status === "SETTLED";
  },

  async openCanonicalCheckout(
    attempt: CanonicalPaymentAttemptResponse,
    method: "UPI" | "CARD",
  ): Promise<void> {
    const intent = attempt.metadata?.providerIntent as
      | { orderId?: unknown; amountPaise?: unknown }
      | undefined;
    const orderId =
      typeof intent?.orderId === "string" ? intent.orderId : undefined;
    if (!orderId)
      throw new Error("Payment provider did not return a checkout order");
    if (Number(intent?.amountPaise) !== Number(attempt.amountPaise))
      throw new Error("Payment checkout amount does not match the attempt");
    const [config, loaded] = await Promise.all([
      this.getRazorpayConfig(),
      this.loadRazorpayScript(),
    ]);
    if (!config.isConfigured || !config.keyId || !loaded)
      throw new Error("Payment checkout is not configured or could not load");
    await new Promise<void>((resolve, reject) => {
      let completed = false;
      const methodName = method.toLowerCase() as "upi" | "card";
      this.openCheckout({
        key: config.keyId,
        amount: Number(attempt.amountPaise),
        currency: "INR",
        name: "MediCorePay",
        description: `${method} sale payment`,
        order_id: orderId,
        config: {
          display: {
            blocks: {
              selected_method: {
                name: `Pay via ${method}`,
                instruments: [{ method: methodName }],
              },
            },
            sequence: ["block.selected_method"],
            preferences: { show_default_blocks: false },
          },
        },
        handler: (response) => {
          if (response.razorpay_order_id !== orderId) {
            reject(new Error("Payment provider returned a different order"));
            return;
          }
          completed = true;
          resolve();
        },
        modal: {
          ondismiss: () => {
            if (!completed) reject(new Error("Payment checkout was cancelled"));
          },
        },
      });
    });
  },

  async waitForCanonicalCapture(
    attemptId: string,
  ): Promise<CanonicalPaymentAttemptResponse> {
    for (let poll = 0; poll < 60; poll += 1) {
      const attempt =
        await productRevivalApi.getCanonicalPaymentAttempt(attemptId);
      if (this.isCapturedCanonicalAttempt(attempt)) return attempt;
      await new Promise((resolve) => window.setTimeout(resolve, 500));
    }
    throw new Error(
      "Payment was submitted but provider confirmation is still pending. Retry the sale to resume this payment safely.",
    );
  },
};
